import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { intlLocale, toLanguage, type Language } from '../i18n.js';
import { AlertIcon, CheckIcon, ClockIcon, GearIcon, XIcon } from './icons.js';
import { BackButton } from './BackButton.js';

export type Grade = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
export type TileStatus = 'good' | 'warn' | 'bad' | 'unknown' | 'setup';

/** Current UI language as the API expects it. */
export function useLocale(): Language {
  const { i18n } = useTranslation();
  return toLanguage(i18n.language);
}

/** Where the full details of an area live (the area page links there). */
export const AREA_DETAILS = {
  availability: '/incidents',
  security: '/findings?category=security',
  links: '/links',
  seo: '/findings?category=seo',
  performance: '/speed',
  email: '/findings?category=email',
  compliance: '/findings?category=compliance',
} as const;

/** Site pages (PRD §5: two navigation levels at most). */
const SECTIONS = [
  ['', 'overview'],
  ['/changes', 'changes'],
  ['/findings', 'findings'],
  ['/incidents', 'incidents'],
  ['/links', 'links'],
  ['/speed', 'speed'],
  ['/ai-search', 'ai'],
  ['/modernization', 'modernization'],
  ['/settings', 'settings'],
] as const;

export function SiteLayout() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const link = ({ isActive }: { isActive: boolean }) =>
    `inline-flex min-h-11 w-full items-center justify-center rounded-md px-3 font-semibold sm:w-auto ${isActive ? 'bg-slate-900 text-white' : 'text-slate-800 hover:bg-slate-100'}`;
  return (
    <>
      <p className="mb-3 print:hidden">
        <BackButton to="/">{t('site.back')}</BackButton>
      </p>
      <nav
        aria-label={t('siteNav.label')}
        className="mb-6 border-b border-slate-200 pb-2 print:hidden"
      >
        <ul className="flex flex-wrap gap-1 [&>li]:flex-auto sm:[&>li]:flex-none">
          {SECTIONS.map(([path, key]) => (
            <li key={key}>
              <NavLink to={`/sites/${siteId}${path}`} end={path === ''} className={link}>
                {t(`siteNav.${key}`)}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <Outlet />
    </>
  );
}

const GRADE_STYLE: Record<Grade, string> = {
  A: 'bg-green-700 text-white',
  B: 'bg-green-600 text-white',
  C: 'bg-amber-300 text-amber-950',
  D: 'bg-orange-400 text-orange-950',
  E: 'bg-red-600 text-white',
  F: 'bg-red-800 text-white',
};

/** Grade letter with the score; the letter is also read out (never colour alone). */
export function GradeBadge({ grade, score }: { grade: Grade; score: number }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-3">
      <span
        aria-hidden="true"
        className={`inline-flex h-16 w-16 items-center justify-center rounded-lg text-4xl font-bold ${GRADE_STYLE[grade]}`}
      >
        {grade}
      </span>
      <span className="sr-only">{t('health.gradeSr', { grade })}</span>
      <span className="text-2xl font-bold">
        {score}
        <span className="text-base font-normal text-slate-700"> / 100</span>
      </span>
    </span>
  );
}

/** Score ranges of the grades (monitoring-rules §6, HEALTH.grades in @website-guard/rules). */
const GRADE_RANGES: readonly [Grade, string][] = [
  ['A', '90–100'],
  ['B', '80–89'],
  ['C', '65–79'],
  ['D', '50–64'],
  ['E', '35–49'],
  ['F', '0–34'],
];

/** All grades as a small scale under the badge, the site's own one marked (owner's idea, 25.09). */
export function GradeScale({ grade }: { grade: Grade }) {
  const { t } = useTranslation();
  return (
    <ol aria-label={t('health.scaleLabel')} className="flex flex-wrap gap-1.5">
      {GRADE_RANGES.map(([g, range]) => {
        const current = g === grade;
        return (
          <li
            key={g}
            title={`${g}: ${t(`health.scale.${g}`)} (${range})`}
            aria-current={current ? 'true' : undefined}
            className="flex w-12 flex-col items-center gap-0.5"
          >
            <span
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-bold ${GRADE_STYLE[g]} ${current ? 'ring-2 ring-slate-900 ring-offset-2' : 'opacity-50'}`}
            >
              {g}
            </span>
            <span
              className={`text-xs tabular-nums ${current ? 'font-semibold text-slate-900' : 'text-slate-600'}`}
            >
              {range}
            </span>
            <span className="sr-only">
              {t(`health.scale.${g}`)}
              {current ? ` – ${t('health.scaleYou')}` : ''}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function TileStatusIcon({ status }: { status: TileStatus }) {
  const { t } = useTranslation();
  const look = {
    good: ['text-green-800', <CheckIcon key="i" />],
    warn: ['text-amber-800', <AlertIcon key="i" />],
    bad: ['text-red-800', <XIcon key="i" />],
    unknown: ['text-slate-600', <ClockIcon key="i" />],
    setup: ['text-violet-800', <GearIcon key="i" />],
  }[status] as [string, ReactNode];
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-semibold ${look[0]}`}>
      {look[1]}
      {t(`tileStatus.${status}`)}
    </span>
  );
}

export function formatPrice(cents: number, language: string) {
  return new Intl.NumberFormat(intlLocale(language), {
    style: 'currency',
    currency: 'EUR',
    currencyDisplay: 'narrowSymbol', // uk-UA would print "49 EUR"; de/en already show €
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/** Invoice amounts: always with cents. */
export function formatMoney(cents: number, language: string) {
  return new Intl.NumberFormat(intlLocale(language), {
    style: 'currency',
    currency: 'EUR',
    currencyDisplay: 'narrowSymbol',
  }).format(cents / 100);
}

/** 32 hex characters; getRandomValues also works outside secure contexts, unlike randomUUID. */
export const randomKey = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');

type ScanType = 'full' | 'crawl' | 'seo_home' | 'performance';

/** Starts a scan by hand with a fresh Idempotency-Key per click. */
export function useStartScan(siteId: string, type: ScanType) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/sites/{siteId}/scans', {
          params: {
            path: { siteId },
            header: { 'idempotency-key': randomKey() },
          },
          body: { type },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', siteId] }),
  });
}

/** Explains why a scan could not be started. */
export function scanError(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    if (error.code === 'FORBIDDEN') return t('scan.unverified');
    if (error.code === 'RATE_LIMITED') return t('scan.limit');
  }
  return errorMessage(error, t as never);
}
